export type ProjectCategory =
  | "web3"
  | "frontend"
  | "fullstack"
  | "mobile"
  | "desktop"
  | "game";
export type ProjectStatus = "active" | "done" | "wip" | "ended";

export interface ProjectBase {
  id: string;
  number: string;
  category: ProjectCategory;
  status: ProjectStatus;
  tags: string[];
  url?: string;
  urlLabel?: string;
}

export interface Project extends ProjectBase {
  title: string;
  description: string;
  statusLabel: string;
}

// Featured projects shown by default, ordered to lead with generalist proof:
// business apps / SaaS -> classic web -> crypto. This balances the showcase
// against the agency positioning instead of opening on 6 Juneo/crypto products.
//
// Keep this list a MULTIPLE OF 6 so the collapsed grid stays full at every
// breakpoint (3 columns -> rows of 3, 2 columns -> rows of 2). 6 fits both;
// 7 left a trailing gap, and any odd count breaks the 2-column layout.
export const FEATURED_PROJECTS: ProjectBase[] = [
  {
    id: "nestohm",
    number: "01",
    category: "fullstack",
    status: "active",
    tags: ["React", "Node.js", "PostgreSQL", "Docker"],
    url: "https://nestohm.com/",
    urlLabel: "nestohm.com",
  },
  {
    id: "covalflow",
    number: "02",
    category: "fullstack",
    status: "active",
    tags: ["React", "TypeScript", "Node.js", "PostgreSQL", "Docker"],
    url: "https://covalflow.com/fr/",
    urlLabel: "covalflow.com",
  },
  {
    id: "naturaSelect",
    number: "03",
    category: "frontend",
    status: "active",
    tags: ["React", "TypeScript", "Shopify", "Next.js"],
    url: "https://natura-select.com/",
    urlLabel: "natura-select.com",
  },
  {
    id: "droitAuCoeur",
    number: "04",
    category: "frontend",
    status: "active",
    tags: ["React", "TypeScript", "GSAP", "i18n"],
    url: "https://droit-au-coeur.com/fr",
    urlLabel: "droit-au-coeur.com",
  },
  {
    id: "supernetBridge",
    number: "05",
    category: "web3",
    status: "ended",
    tags: ["React", "Solidity", "Wagmi", "Docker"],
  },
  {
    id: "juneoPortal",
    number: "06",
    category: "web3",
    status: "ended",
    tags: ["React", "Ethers.js", "Node.js", "WebSocket"],
  },
];

// Remaining projects revealed via "show more", kept in the same
// apps -> web -> crypto -> games hierarchy as the featured set.
export const MORE_PROJECTS: ProjectBase[] = [
  {
    id: "lampshadeStudio",
    number: "07",
    category: "frontend",
    status: "active",
    tags: [
      "React Router 7",
      "TypeScript",
      "WebGL",
      "Cloudflare Workers",
      "STL",
    ],
    url: "https://studio-cocha.com/lampshade",
    urlLabel: "studio-cocha.com",
  },
  {
    id: "juneoWebWallet",
    number: "08",
    category: "web3",
    status: "ended",
    tags: ["React", "TypeScript", "JuneoJS", "Zustand"],
  },
  {
    id: "mcnNodeManager",
    number: "09",
    category: "desktop",
    status: "ended",
    tags: ["TypeScript", "Electron", "JuneoGo", "Node"],
  },
  {
    id: "fourByFourVert",
    number: "10",
    category: "frontend",
    status: "done",
    tags: ["React", "GSAP", "TypeScript"],
    url: "https://4x4vert.be/",
    urlLabel: "4x4vert.be",
  },
  {
    id: "falcohmSystem",
    number: "11",
    category: "frontend",
    status: "active",
    tags: ["React", "GSAP", "TypeScript"],
    url: "http://falcohmsystem.com/",
    urlLabel: "falcohmsystem.com",
  },
  {
    id: "gazmatek",
    number: "12",
    category: "frontend",
    status: "active",
    tags: ["React", "GSAP", "TypeScript"],
    url: "https://www.gazmatek.com/en/",
    urlLabel: "gazmatek.com",
  },
  {
    id: "juneoWebsite",
    number: "13",
    category: "frontend",
    status: "ended",
    tags: ["React", "GSAP", "i18n", "Vite"],
  },
  {
    id: "juneoWalletExtension",
    number: "14",
    category: "web3",
    status: "ended",
    tags: ["React", "Chrome API", "JuneoJS"],
    url: "https://chromewebstore.google.com/detail/juneo-wallet/bmbgpekfibflcgcojpalgfohfnbebofl",
    urlLabel: "Chrome Store",
  },
  {
    id: "juneoMobileWallet",
    number: "15",
    category: "mobile",
    status: "ended",
    tags: ["React Native", "Expo", "Biometrics"],
  },
  {
    id: "mcnAuction",
    number: "16",
    category: "web3",
    status: "ended",
    tags: ["Solidity", "JuneoJS", "Dutch Auction", "P-Chain"],
  },
  {
    id: "juneoOnChainBots",
    number: "17",
    category: "web3",
    status: "ended",
    tags: ["TypeScript", "JuneoJS", "MEV", "DEX", "Arbitrage"],
  },
  {
    id: "juneoWhaleAlert",
    number: "18",
    category: "web3",
    status: "ended",
    tags: ["TypeScript", "JuneoJS", "Telegram Bot"],
  },
  {
    id: "juneoMultisig",
    number: "19",
    category: "web3",
    status: "ended",
    tags: ["React", "Solidity", "JuneoJS", "Gnosis Safe", "Multisig"],
  },
  {
    id: "cortex",
    number: "20",
    category: "web3",
    status: "wip",
    tags: ["TypeScript", "Node.js", "Blockchain", "Staking", "Docker"],
  },
  {
    id: "wpmc",
    number: "21",
    category: "game",
    status: "wip",
    tags: ["Java", "Minecraft", "Spigot", "MySQL"],
  },
  {
    id: "wps",
    number: "22",
    category: "game",
    status: "wip",
    tags: ["C#", "Unity", "RTS", "Multiplayer"],
  },
  {
    id: "projectTribes",
    number: "23",
    category: "game",
    status: "wip",
    tags: ["C#", "Unity", "Strategy", "Multiplayer"],
  },
];

export const ALL_PROJECTS: ProjectBase[] = [
  ...FEATURED_PROJECTS,
  ...MORE_PROJECTS,
];

export const VITRINE_IDS = [
  "droitAuCoeur",
  "falcohmSystem",
  "gazmatek",
  "fourByFourVert",
  "juneoWebsite",
] as const;

export type VitrineId = (typeof VITRINE_IDS)[number];

export interface VitrineTarget {
  id: VitrineId;
  url: string;
}

export const getVitrineProjects = (): ProjectBase[] =>
  VITRINE_IDS.map((id) => ALL_PROJECTS.find((p) => p.id === id)).filter(
    (p): p is ProjectBase => p !== undefined,
  );

export const getVitrineTargets = (): VitrineTarget[] =>
  getVitrineProjects()
    .filter((p): p is ProjectBase & { url: string } => Boolean(p.url))
    .map((p) => ({ id: p.id as VitrineId, url: p.url }));
